<template>
	<div class="campaign-email-report-page">
		<div class="report-container container">
			<div class="page-header d-flex justify-content-between align-items-center my-4 flex-wrap gap-3">
				<div class="d-flex align-items-center">
					<router-link :to="backRoute" class="back-btn me-3" title="Back" aria-label="Back">
						<i class="fad fa-arrow-left"></i>
					</router-link>
					<div>
						<h2 class="page-title">{{ (report.campaign && report.campaign.name) || 'Campaign Email Report' }}</h2>
						<p class="page-subtitle">
							{{ reportSubtitle }}
						</p>
					</div>
				</div>
				<div class="report-actions">
					<button type="button" class="back-btn" title="Refresh" aria-label="Refresh report" @click="loadReport" :disabled="loading">
						<i :class="['fad', 'fa-sync', { 'fa-spin': loading }]"></i>
					</button>
				</div>
			</div>

			<div v-if="loading" class="loading-state">
				<div class="spinner-border text-primary" role="status"></div>
				<p class="mt-3 text-muted">Loading email report...</p>
			</div>

			<div v-else class="report-content">
				<div class="overview-grid">
					<div class="metric-card">
						<div class="metric-value">{{ report.totals.total || 0 }}</div>
						<div class="metric-label">Total Emails</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-success">{{ report.totals.delivered || 0 }}</div>
						<div class="metric-label">Delivered</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-info">{{ report.totals.opened || 0 }}</div>
						<div class="metric-label">Opened</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-primary">{{ report.totals.clicked || 0 }}</div>
						<div class="metric-label">Clicked</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-warning">{{ report.totals.retrying || 0 }}</div>
						<div class="metric-label">Retrying</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-danger">{{ failedTotal }}</div>
						<div class="metric-label">Failed / Bounced</div>
					</div>
				</div>

				<div class="row g-4 mt-1">
					<div class="col-lg-8">
						<div class="report-card">
							<div class="report-card-header">
								<h3 class="report-card-title">Performance Rates</h3>
								<span class="report-card-meta">Generated {{ formatDateTime(report.generated_at) }}</span>
							</div>
							<div class="rates-grid">
								<div class="rate-item">
									<div class="rate-label">Delivery Rate</div>
									<div class="rate-value">{{ formatPercent(report.rates.delivery_rate) }}</div>
								</div>
								<div class="rate-item">
									<div class="rate-label">Open Rate</div>
									<div class="rate-value">{{ formatPercent(report.rates.open_rate) }}</div>
								</div>
								<div class="rate-item">
									<div class="rate-label">Click Rate</div>
									<div class="rate-value">{{ formatPercent(report.rates.click_rate) }}</div>
								</div>
								<div class="rate-item">
									<div class="rate-label">Failure Rate</div>
									<div class="rate-value">{{ formatPercent(report.rates.failure_rate) }}</div>
								</div>
							</div>
						</div>

						<div class="report-card mt-4">
							<div class="report-card-header">
								<h3 class="report-card-title">Daily Activity</h3>
							</div>
							<div v-if="report.activity.length" class="activity-table">
								<div class="table-header">
									<div class="header-cell">Date</div>
									<div class="header-cell text-center">Total</div>
									<div class="header-cell text-center">Sent</div>
									<div class="header-cell text-center">Delivered</div>
									<div class="header-cell text-center">Opened</div>
									<div class="header-cell text-center">Clicked</div>
									<div class="header-cell text-center">Failed</div>
								</div>
								<div class="table-row" v-for="row in report.activity" :key="row.activity_date">
									<div class="table-cell">{{ formatShortDate(row.activity_date) }}</div>
									<div class="table-cell text-center">{{ row.total }}</div>
									<div class="table-cell text-center">{{ row.sent }}</div>
									<div class="table-cell text-center">{{ row.delivered }}</div>
									<div class="table-cell text-center">{{ row.opened }}</div>
									<div class="table-cell text-center">{{ row.clicked }}</div>
									<div class="table-cell text-center">{{ row.failed }}</div>
								</div>
							</div>
							<div v-else class="empty-state">No email activity yet.</div>
						</div>
					</div>

					<div class="col-lg-4">
						<div class="report-card">
							<div class="report-card-header">
								<h3 class="report-card-title">Providers</h3>
							</div>
							<div v-if="report.providers.length" class="stack-list">
								<div class="stack-item" v-for="provider in report.providers" :key="provider.provider || 'unknown'">
									<span>{{ provider.provider || 'Unknown' }}</span>
									<strong>{{ provider.total }}</strong>
								</div>
							</div>
							<div v-else class="empty-state small">No provider data.</div>
						</div>

						<div class="report-card mt-4">
							<div class="report-card-header">
								<h3 class="report-card-title">Templates</h3>
							</div>
							<div v-if="report.templates.length" class="stack-list">
								<div class="stack-item" v-for="template in report.templates" :key="template.email_template_id || template.name">
									<span>{{ template.name }}</span>
									<strong>{{ template.total }}</strong>
								</div>
							</div>
							<div v-else class="empty-state small">No template data.</div>
						</div>
					</div>
				</div>

				<div class="report-card mt-4">
					<div class="report-card-header">
						<h3 class="report-card-title">Recent Email Items</h3>
					</div>
					<div v-if="report.recent_items.length" class="activity-table">
						<div class="table-header">
							<div class="header-cell">Recipient</div>
							<div class="header-cell">Municipality</div>
							<div class="header-cell">Template</div>
							<div class="header-cell">Status</div>
							<div class="header-cell text-center">Attempts</div>
							<div class="header-cell">Next Retry</div>
						</div>
						<div class="table-row" v-for="item in report.recent_items" :key="item.id">
							<div class="table-cell">
								<div class="recipient-main">{{ item.recipient_name || 'No name' }}</div>
								<div class="recipient-sub">{{ item.recipient_email }}</div>
							</div>
							<div class="table-cell">{{ (item.municipality && item.municipality.name) || 'N/A' }}</div>
							<div class="table-cell">{{ (item.email_template && item.email_template.name) || 'Snapshot' }}</div>
							<div class="table-cell">
								<span class="status-pill" :class="`status-${item.status}`">{{ item.status }}</span>
							</div>
							<div class="table-cell text-center">{{ item.attempt_count || 0 }} / {{ item.max_attempts || 5 }}</div>
							<div class="table-cell">{{ formatDateTime(item.next_attempt_at) }}</div>
						</div>
					</div>
					<div v-else class="empty-state">No email items generated for this campaign yet.</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '@/api/index'

export default {
	name: 'CampaignEmailReport',
	data() {
		return {
			loading: false,
			report: {
				campaign: {},
				totals: {},
				rates: {},
				providers: [],
				templates: [],
				activity: [],
				recent_items: [],
				generated_at: null,
			},
		}
	},
	computed: {
		campaignType() {
			return this.$route.params.campaignType
		},
		campaignId() {
			return this.$route.params.campaignId
		},
		backRoute() {
			return this.campaignType === 'sequence'
				? { name: 'editAutoCampaignWithSequences', params: { id: this.campaignId } }
				: { name: 'autoCampaignRuns', params: { id: this.campaignId } }
		},
		reportSubtitle() {
			if (this.campaignType === 'sequence') {
				return 'Sequenced campaign email metrics and delivery activity'
			}

			return 'Auto campaign email metrics and delivery activity'
		},
		failedTotal() {
			return (this.report.totals.failed || 0) + (this.report.totals.bounced || 0)
		},
	},
	watch: {
		'$route.params': {
			deep: true,
			handler() {
				this.loadReport()
			},
		},
	},
	async mounted() {
		await this.loadReport()
	},
	methods: {
		getEndpoint() {
			return this.campaignType === 'sequence'
				? `/sequenced-campaigns/${this.campaignId}/email-report`
				: `/auto-campaigns/${this.campaignId}/email-report`
		},
		async loadReport() {
			this.loading = true
			try {
				const response = await restApi.get(this.getEndpoint())
				this.report = {
					campaign: response.data?.campaign || {},
					totals: response.data?.totals || {},
					rates: response.data?.rates || {},
					providers: response.data?.providers || [],
					templates: response.data?.templates || [],
					activity: response.data?.activity || [],
					recent_items: response.data?.recent_items || [],
					generated_at: response.data?.generated_at || null,
				}
			} catch (error) {
				console.error('Error loading campaign email report:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to load email report',
					text: error.response?.data?.message || error.message || 'An error occurred while loading the report.',
				})
			} finally {
				this.loading = false
			}
		},
		formatPercent(value) {
			return `${parseFloat(value || 0).toFixed(2)}%`
		},
		formatShortDate(value) {
			if (!value) return 'N/A'
			return new Date(value).toLocaleDateString()
		},
		formatDateTime(value) {
			if (!value) return 'N/A'
			return new Date(value).toLocaleString()
		},
	},
}
</script>

<style scoped>
.campaign-email-report-page {
	padding-bottom: 32px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	padding: 0;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
}

.back-btn:hover:not(:disabled) {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.back-btn:disabled {
	opacity: 0.6;
	cursor: default;
}

.page-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.page-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.loading-state,
.empty-state {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 48px 24px;
	text-align: center;
}

.empty-state.small {
	padding: 20px;
}

.overview-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 16px;
}

.metric-card,
.report-card {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 20px;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.metric-value {
	font-size: 2rem;
	font-weight: 700;
	line-height: 1;
}

.metric-label {
	margin-top: 8px;
	color: #6b7280;
	font-size: 0.95rem;
}

.report-card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
}

.report-card-title {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 700;
}

.report-card-meta {
	color: #6b7280;
	font-size: 0.85rem;
}

.rates-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
}

.rate-item {
	background: #f8fafc;
	border-radius: 12px;
	padding: 16px;
}

.rate-label {
	color: #64748b;
	font-size: 0.9rem;
	margin-bottom: 6px;
}

.rate-value {
	font-size: 1.5rem;
	font-weight: 700;
}

.stack-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.stack-item,
.table-header,
.table-row {
	display: grid;
	grid-template-columns: repeat(6, minmax(0, 1fr));
	align-items: center;
	gap: 12px;
}

.stack-item {
	grid-template-columns: minmax(0, 1fr) auto;
	background: #f8fafc;
	padding: 12px 14px;
	border-radius: 12px;
}

.activity-table {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.table-header {
	color: #64748b;
	font-size: 0.8rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.table-row {
	background: #f8fafc;
	border-radius: 12px;
	padding: 14px;
}

.table-cell {
	min-width: 0;
	word-break: break-word;
}

.text-center {
	text-align: center;
}

.recipient-main {
	font-weight: 600;
}

.recipient-sub {
	color: #6b7280;
	font-size: 0.85rem;
	margin-top: 4px;
}

.status-pill {
	display: inline-flex;
	align-items: center;
	padding: 6px 10px;
	border-radius: 999px;
	font-size: 0.8rem;
	font-weight: 700;
	text-transform: capitalize;
}

.status-sent,
.status-delivered,
.status-opened,
.status-clicked {
	background: #dcfce7;
	color: #166534;
}

.status-scheduled,
.status-queued,
.status-prepared {
	background: #dbeafe;
	color: #1d4ed8;
}

.status-failed,
.status-bounced {
	background: #fee2e2;
	color: #b91c1c;
}

@media (max-width: 991px) {
	.table-header,
	.table-row {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.rates-grid {
		grid-template-columns: 1fr;
	}
}
</style>
